/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { LearnerProfile_ChallengeAchievement as ChallengeAchievementType } from '__generated__/graphql-types-do-not-use';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Grid, Typography2 } from '@coursera/cds-core';
import { TrophyIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/account-profile';

const styles = {
  achievementBadge: css`
    display: inline-flex;
    width: 100px;
    height: 22px;
    padding: 0 8px 0 4px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
    border-radius: 4px;
    border: 1px solid var(--cds-color-callouts-tertiary);
    background: var(--cds-color-callouts-tertiary);
  `,
  achievementBadgeIcon: css`
    color: var(--cds-color-neutral-primary-invert);
  `,
  achievementBadgeText: css`
    color: var(--cds-color-neutral-primary-invert);
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: -0.1px;
  `,
};

const PERCENTILE_CUTOFF = 21;

export const getAchievementData = (
  achievement: ChallengeAchievementType | null | undefined
): ChallengeAchievementType | null => {
  if (!achievement) {
    return null;
  }

  let percentile = achievement.percentile;

  // Only return achievement data for top 5%, 10%, and 20% of learners
  // We floor it to the nearest rank if percentile is not more than 1 away from the rank's cutoff.
  if (!percentile || percentile < 0 || percentile > PERCENTILE_CUTOFF) {
    return null;
  } else if (percentile < 6) {
    percentile = 5;
  } else if (percentile >= 6 && percentile < 11) {
    percentile = 10;
  } else {
    percentile = 20;
  }

  return {
    ...achievement,
    percentile,
  };
};

export const Badge = (props: { achievement?: ChallengeAchievementType | null }) => {
  const { achievement } = props;

  const achievementData = getAchievementData(achievement);

  return (
    achievementData && (
      <Grid css={styles.achievementBadge}>
        <TrophyIcon size="small" css={styles.achievementBadgeIcon} />
        <Typography2 component="h4" variant="subtitleMedium" css={styles.achievementBadgeText}>
          <FormattedMessage message={_t('Top {percentile}%')} percentile={achievementData.percentile} />
        </Typography2>
      </Grid>
    )
  );
};

type RankMessageProps = {
  learnerName?: string | null;
  achievement?: ChallengeAchievementType | null;
};

export const RankMessage = ({ learnerName, achievement }: RankMessageProps) => {
  const achievementData = getAchievementData(achievement);

  if (!achievementData) {
    return null;
  }

  const { percentile, totalNumberOfParticipants } = achievementData;

  return learnerName ? (
    <FormattedMessage
      message={_t('{fullName} ranked top {percentile}% out of {participants} participants')}
      fullName={learnerName}
      percentile={percentile}
      participants={totalNumberOfParticipants}
    />
  ) : (
    <FormattedMessage
      message={_t('Ranked top {percentile}% out of {participants} participants')}
      percentile={percentile}
      participants={totalNumberOfParticipants}
    />
  );
};

export const ChallengeAchievement = (props: {
  label: JSX.Element;
  learnerName: string | null | undefined;
  achievement: ChallengeAchievementType;
}) => {
  const { label, learnerName, achievement } = props;

  const achievementData = getAchievementData(achievement);

  return (
    achievementData && (
      <>
        {label}
        <Badge achievement={achievement} />
        <Typography2 component="p">
          <RankMessage learnerName={learnerName} achievement={achievementData} />
        </Typography2>
      </>
    )
  );
};
